import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';

// ============================================
// 캘린더 헤더 컨트롤 공통 styled (private + group lesson 공유)
// 디자인 기준: privateLesson (#4AB3BC 계열)
// ============================================

// Brand colors (lesson 캘린더 컨트롤 한정)
const PRIMARY = '#4AB3BC';
const PRIMARY_HOVER = '#4AB3BC';
const PRIMARY_BG = 'rgba(15, 127, 133, 0.1)';
const WHITE = '#FFFFFF';

// ============================================
// Buttons
// ============================================
export const TodayButton = styled(Button)({
  background: PRIMARY,
  color: WHITE,
  '&:hover': { background: PRIMARY_HOVER },
  borderRadius: '8px',
  padding: '0 16px',
  height: '36px',
  fontSize: '16px',
});

export const NavButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ZoomButtonGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '2px',
});

export const ZoomButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isDisabled',
})(({ isDisabled }) => ({
  minWidth: '32px',
  width: '32px',
  height: '32px',
  padding: 0,
  borderRadius: '6px',
  fontSize: '20px',
  fontWeight: 600,
  color: isDisabled ? '#ccc' : '#666',
  cursor: isDisabled ? 'default' : 'pointer',
  '&:hover': {
    backgroundColor: isDisabled ? 'transparent' : '#f0f0f0',
  },
}));

export const ZoomLabel = styled(Typography)({
  fontSize: '18px',
  color: '#999',
  minWidth: '36px',
  textAlign: 'center',
  userSelect: 'none',
});

export const CalendarPickerButton = styled('button')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  backgroundColor: '#fff',
  color: '#737373',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    color: PRIMARY,
    borderColor: PRIMARY,
  },
});

// 헤더 좌측 "+ 일정 추가" — 날짜 피커 옆에서 생성 모달을 연다 (주간·월간 패널의 같은 이름 버튼과 동작 동일)
export const HeaderAddScheduleButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  height: '36px',
  padding: '0 14px',
  fontSize: '14px',
  // '스케줄 설정'·'오늘'과 같은 흰 배경 + 아웃라인, 글자는 필터 버튼(FilterButton)과 같은 청록·굵기
  fontWeight: 500,
  color: PRIMARY,
  background: WHITE,
  border: '1px solid #9FD3D7',
  borderRadius: '8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s ease',
  '&:hover': { backgroundColor: 'rgba(74, 179, 188, 0.08)' },
});

// 기간 조회 활성화 순간 1회 퍼지는 펄스 — 테두리 강조와 함께 상태 전환을 알린다
const rangeActivePulse = keyframes({
  from: { boxShadow: '0 0 0 0 rgba(74, 179, 188, 0.45)' },
  to: { boxShadow: '0 0 0 8px rgba(74, 179, 188, 0)' },
});

// 기간 조회 버튼 — 기간이 선택돼 있으면 브랜드색 테두리로 활성 표시
export const RangePickerButton = styled(CalendarPickerButton, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  ...(isActive && {
    color: PRIMARY,
    borderColor: PRIMARY,
    borderWidth: '2px',
    backgroundColor: PRIMARY_BG,
    animation: `${rangeActivePulse} 700ms ease-out`,
  }),
}));

// 기간 조회 초기화 버튼 — 기간이 선택된 동안에만 노출
export const RangeResetButton = styled('button')({
  height: '36px',
  padding: '0 12px',
  border: `1px solid ${PRIMARY}`,
  borderRadius: '8px',
  backgroundColor: WHITE,
  color: PRIMARY,
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: PRIMARY_BG },
});

export const FilterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  background: isActive ? PRIMARY : WHITE,
  color: isActive ? WHITE : PRIMARY,
  border: `1px solid ${PRIMARY}`,
  height: '36px',
  '&:hover': {
    background: isActive ? PRIMARY_HOVER : PRIMARY_BG,
  },
  fontSize: '16px',
}));

export const SettingsButton = styled(Button)({
  background: PRIMARY,
  color: WHITE,
  border: `1px solid ${PRIMARY}`,
  height: '36px',
  '&:hover': {
    background: 'none',
    color: PRIMARY,
  },
  fontSize: '16px',
});

export const ViewToggleContainer = styled(Box)({
  display: 'flex',
  border: `1px solid ${PRIMARY}`,
  borderRadius: '8px',
  overflow: 'hidden',
  height: '36px',
});

export const ViewToggleButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isRight',
})(({ isActive, isRight }) => ({
  background: isActive ? PRIMARY : WHITE,
  color: isActive ? WHITE : PRIMARY,
  border: 'none',
  borderRadius: 0,
  minWidth: '70px',
  borderLeft: isRight ? `1px solid ${PRIMARY}` : 'none',
  '&:hover': {
    background: isActive ? PRIMARY_HOVER : PRIMARY_BG,
  },
  fontSize: '16px',
}));

export const ViewToggleButtonWithBorder = styled(ViewToggleButton)({
  borderLeft: `1px solid ${PRIMARY}`,
});
